/** Why the last screen share attempt produced nothing — rendered as a hint under the controls. */
export type ScreenShareHint = 'permission' | 'failed';

/**
 * What a failed getDisplayMedia means for the user.
 *
 * `NotAllowedError` covers two cases we cannot tell apart: the user opened the
 * picker and closed it, and the OS refusing the capture without ever rendering a
 * picker. Timing does not separate them — Safari takes about a second to refuse,
 * which is exactly how long a quick dismissal takes — so both get the same hint,
 * worded to fit either. Staying silent on the guess that it was a dismissal is
 * what hid a real refusal for a whole round of testing.
 */
export const classifyScreenShareFailure = (error: unknown): ScreenShareHint =>
  error instanceof DOMException && error.name === 'NotAllowedError' ? 'permission' : 'failed';

/**
 * Error identity for the Sentry report — every refusal gets logged, none are assumed benign.
 *
 * `activation` здесь не для полноты: `NotAllowedError` одинаково выглядит и когда человек закрыл
 * окно выбора, и когда браузер отказал сам, не показав его. Различает эти случаи только состояние
 * «живого жеста» в момент запроса — без него окна выбора не бывает по определению.
 */
export const describeScreenShareFailure = (
  error: unknown,
  elapsedMs: number,
  activation?: string
): Record<string, string | number> => ({
  elapsedMs,
  name: error instanceof Error ? error.name : typeof error,
  message: error instanceof Error ? error.message : String(error),
  ...(activation ? { activation } : {}),
});
